import React, { useState, useEffect, useRef } from 'react';
import { geminiService, ExtractedItem, ExtractedReceipt, ReceiptImageInput } from '../../apiServices/geminiService';
import { categoryService, accountService } from '../../apiServices';
import { receiptService } from '../../apiServices/receiptService';
import { Category, Account, PositionSuggestionResponse } from '../../apiServices/types';
import { useNotification } from '../../contexts/NotificationContext';
import { formatCurrency, toLocalISOString } from '../../utils';
import { Modal, GlassSelect, ConfirmationModal } from '../UI';
import PositionSuggestionsPanel from '../pages/ExpensesPage/components/PositionSuggestionsPanel';
import './ReceiptOCRModal.css';

interface ReceiptOCRModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReceiptCreated?: (receiptId: string) => void;
  apiKey?: string;
  initialReceiptText?: string;
  initialReceipts?: ExtractedReceipt[];
  initialImages?: File[]; // open modal with N image pool items (one per photo)
}

interface ItemWithCategory extends ExtractedItem {
  categoryId?: string;
  id: string;
}

interface ReceiptPoolItem {
  id: string;
  type: 'image' | 'text';
  imageFile?: File;
  imagePreviewUrl?: string;
  extraImageFiles?: File[];       // additional images for multi-image "один чек" mode
  extraImagePreviewUrls?: string[];
  receiptText: string;
  items: ItemWithCategory[];
  receiptName: string;
  accountId: string;
  receiptDate: string; // YYYY-MM-DD
  status: 'pending' | 'processing' | 'done' | 'error' | 'created';
  isCreating: boolean;
}

const todayDateStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

let _poolCounter = 0;
const newPoolId = () => `pool-${++_poolCounter}-${Date.now()}`;

const makePoolItem = (
  type: 'image' | 'text',
  overrides: Partial<ReceiptPoolItem> = {}
): ReceiptPoolItem => ({
  id: newPoolId(),
  type,
  receiptText: '',
  items: [],
  receiptName: '',
  accountId: '',
  receiptDate: todayDateStr(),
  status: 'pending',
  isCreating: false,
  ...overrides,
});

const ReceiptOCRModal: React.FC<ReceiptOCRModalProps> = ({
  isOpen,
  onClose,
  onReceiptCreated,
  apiKey,
  initialReceiptText,
  initialReceipts,
  initialImages,
}) => {
  const { showNotification } = useNotification();

  // ── Pool state ──────────────────────────────────────────────────────────────
  const [poolItems, setPoolItems] = useState<ReceiptPoolItem[]>([]);
  const [activePoolId, setActivePoolId] = useState<string>('');

  // ── Shared state (not per-item) ─────────────────────────────────────────────
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [bulkCategoryId, setBulkCategoryId] = useState<string>('');
  const [editingCategoryItemId, setEditingCategoryItemId] = useState<string | null>(null);
  const [editingNameItemId, setEditingNameItemId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState<string>('');
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState<string | null>(null);
  const [inputStyles, setInputStyles] = useState<React.CSSProperties>({});
  // Whether we're replacing an existing item's image (stores the pool item id)
  const [replacingImageId, setReplacingImageId] = useState<string | null>(null);
  const [pendingDragOver, setPendingDragOver] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const addImageInputRef = useRef<HTMLInputElement>(null);
  const replaceImageInputRef = useRef<HTMLInputElement>(null);

  // ── Derived state from active pool item ────────────────────────────────────
  const activeItem = poolItems.find(p => p.id === activePoolId);
  const items = activeItem?.items ?? [];
  const receiptText = activeItem?.receiptText ?? '';
  const receiptName = activeItem?.receiptName ?? '';
  const selectedAccountId = activeItem?.accountId ?? '';
  const receiptDate = activeItem?.receiptDate ?? todayDateStr();
  const isProcessing = activeItem?.status === 'processing';
  const isCreating = activeItem?.isCreating ?? false;

  // ── Helpers ────────────────────────────────────────────────────────────────
  const updateActiveItem = (updates: Partial<ReceiptPoolItem>) => {
    setPoolItems(prev => prev.map(p => p.id === activePoolId ? { ...p, ...updates } : p));
  };

  const updatePoolItem = (id: string, updates: Partial<ReceiptPoolItem>) => {
    setPoolItems(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const sortItems = (itemsToSort: ItemWithCategory[]): ItemWithCategory[] => {
    return [...itemsToSort].sort((a, b) => {
      const categoryA = a.categoryId ? categories.find(c => c.id === a.categoryId) : null;
      const categoryB = b.categoryId ? categories.find(c => c.id === b.categoryId) : null;
      if (categoryA && !categoryB) return -1;
      if (!categoryA && categoryB) return 1;
      if (categoryA && categoryB) {
        const nameCompare = categoryA.name.localeCompare(categoryB.name, 'uk-UA');
        if (nameCompare !== 0) return nameCompare;
      }
      return a.name.localeCompare(b.name, 'uk-UA');
    });
  };

  // ── Initialise pool when modal opens ───────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadAccounts();

      let firstItems: ReceiptPoolItem[];

      if (initialImages && initialImages.length > 0) {
        // Multiple images from GeminiOCRPage → one pool item per image
        firstItems = initialImages.map(file => makePoolItem('image', {
          imageFile: file,
          imagePreviewUrl: URL.createObjectURL(file),
          receiptName: file.name.replace(/\.[^/.]+$/, ''),
        }));
      } else {
        // Single text/pre-processed item(s)
        const preReceipts = initialReceipts?.length ? initialReceipts : [undefined];
        firstItems = preReceipts.map((r, ri) => {
          const poolItemsList: ItemWithCategory[] = (r?.items ?? []).map((item, index) => ({
            ...item,
            id: `temp-${ri}-${index}-${Date.now()}`,
            categoryId: item.categoryId,
          }));
          return makePoolItem('text', {
            receiptText: ri === 0 ? (initialReceiptText || '') : '',
            receiptName: r?.receiptName || '',
            ...(r?.receiptDate ? { receiptDate: r.receiptDate } : {}),
            ...(r?.accountId ? { accountId: r.accountId } : {}),
            items: poolItemsList,
            status: poolItemsList.length > 0 ? 'done' : 'pending',
          });
        });
      }

      setPoolItems(firstItems);
      setActivePoolId(firstItems[0].id);
      setSelectedItems(new Set());
      setBulkCategoryId('');
      setEditingCategoryItemId(null);
      setEditingNameItemId(null);
      setEditingNameValue('');
      setShowConfirmClose(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set default account for all pool items that have none when accounts load
  useEffect(() => {
    if (accounts.length > 0) {
      const defaultAccount = accounts.find(a => a.isDefault) || accounts[0];
      setPoolItems(prev =>
        prev.map(p => p.accountId ? p : { ...p, accountId: defaultAccount.id })
      );
    }
  }, [accounts]);

  // Reset editing state when switching between pool items
  useEffect(() => {
    setEditingCategoryItemId(null);
    setEditingNameItemId(null);
    setEditingNameValue('');
    setSelectedItems(new Set());
    setBulkCategoryId('');
    setHighlightedSuggestion(null);
  }, [activePoolId]);

  // ── Paste image → add to active pool item ─────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e: ClipboardEvent) => {
      const clipItems = e.clipboardData?.items;
      if (!clipItems) return;
      for (let i = 0; i < clipItems.length; i++) {
        if (clipItems[i].type.startsWith('image/')) {
          const file = clipItems[i].getAsFile();
          if (file) {
            e.preventDefault();
            const url = URL.createObjectURL(file);
            setPoolItems(prev => prev.map(p => {
              if (p.id !== activePoolId) return p;
              if (!p.imageFile) return { ...p, imageFile: file, imagePreviewUrl: url, type: 'image' as const };
              return { ...p, extraImageFiles: [...(p.extraImageFiles || []), file], extraImagePreviewUrls: [...(p.extraImagePreviewUrls || []), url] };
            }));
          }
          break;
        }
      }
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen, activePoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAll();
      setCategories(data);
    } catch (error) {
      showNotification('Помилка завантаження категорій', 'error');
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService.getAll();
      setAccounts(data);
    } catch (error) {
      showNotification('Помилка завантаження рахунків', 'error');
    }
  };

  // ── Pool management ─────────────────────────────────────────────────────────
  const getDefaultAccountId = () => {
    const defaultAccount = accounts.find(a => a.isDefault) || accounts[0];
    return defaultAccount?.id || '';
  };

  const addImageToActiveItem = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) { showNotification('Зображення завелике. Максимум 10MB', 'warning'); return; }
    const url = URL.createObjectURL(file);
    setPoolItems(prev => prev.map(p => {
      if (p.id !== activePoolId) return p;
      if (!p.imageFile) return { ...p, imageFile: file, imagePreviewUrl: url, type: 'image' as const };
      return { ...p, extraImageFiles: [...(p.extraImageFiles || []), file], extraImagePreviewUrls: [...(p.extraImagePreviewUrls || []), url] };
    }));
  };

  const removeImageFromActiveItem = (index: number) => {
    if (!activeItem) return;
    const allFiles = [activeItem.imageFile!, ...(activeItem.extraImageFiles || [])];
    const allPreviews = [activeItem.imagePreviewUrl!, ...(activeItem.extraImagePreviewUrls || [])];
    URL.revokeObjectURL(allPreviews[index]);
    const newFiles = allFiles.filter((_, i) => i !== index);
    const newPreviews = allPreviews.filter((_, i) => i !== index);
    if (newFiles.length === 0) {
      updateActiveItem({ imageFile: undefined, imagePreviewUrl: undefined, extraImageFiles: [], extraImagePreviewUrls: [] });
    } else {
      updateActiveItem({ imageFile: newFiles[0], imagePreviewUrl: newPreviews[0], extraImageFiles: newFiles.slice(1), extraImagePreviewUrls: newPreviews.slice(1) });
    }
  };

  const handleAddNewReceipt = () => {
    const newItem = makePoolItem('image', { accountId: getDefaultAccountId() });
    setPoolItems(prev => [...prev, newItem]);
    setActivePoolId(newItem.id);
  };

  const handleAddImageItems = () => {
    setReplacingImageId(null);
    addImageInputRef.current?.click();
  };

  const handleAddTextItem = () => {
    const newItem = makePoolItem('text', { accountId: getDefaultAccountId() });
    setPoolItems(prev => [...prev, newItem]);
    setActivePoolId(newItem.id);
  };

  const handleImageFilesAdded = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const newItems: ReceiptPoolItem[] = Array.from(files).map(file => makePoolItem('image', {
      imageFile: file,
      imagePreviewUrl: URL.createObjectURL(file),
      receiptName: file.name.replace(/\.[^/.]+$/, ''),
      accountId: getDefaultAccountId(),
    }));
    setPoolItems(prev => [...prev, ...newItems]);
    setActivePoolId(newItems[newItems.length - 1].id);
    e.target.value = '';
  };

  const handleReplaceImage = (poolItemId: string) => {
    setReplacingImageId(poolItemId);
    replaceImageInputRef.current?.click();
  };

  const handleImageFileReplaced = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !replacingImageId) return;
    const old = poolItems.find(p => p.id === replacingImageId);
    if (old?.imagePreviewUrl) URL.revokeObjectURL(old.imagePreviewUrl);
    updatePoolItem(replacingImageId, {
      imageFile: file,
      imagePreviewUrl: URL.createObjectURL(file),
      receiptName: file.name.replace(/\.[^/.]+$/, ''),
      status: 'pending',
      items: [],
    });
    setReplacingImageId(null);
    e.target.value = '';
  };

  const handleRemovePoolItem = (poolItemId: string) => {
    setPoolItems(prev => {
      const filtered = prev.filter(p => p.id !== poolItemId);
      if (filtered.length === 0) {
        // Keep at least 1 text item
        const fallback = makePoolItem('text', { accountId: getDefaultAccountId() });
        setActivePoolId(fallback.id);
        return [fallback];
      }
      if (activePoolId === poolItemId) {
        const idx = prev.findIndex(p => p.id === poolItemId);
        setActivePoolId(filtered[Math.min(idx, filtered.length - 1)].id);
      }
      return filtered;
    });
    // Revoke object URL if image
    const item = poolItems.find(p => p.id === poolItemId);
    if (item?.imagePreviewUrl) URL.revokeObjectURL(item.imagePreviewUrl);
  };

  const handleShiftDate = (days: number) => {
    const cur = new Date(receiptDate + 'T00:00:00');
    cur.setDate(cur.getDate() + days);
    const str = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    updateActiveItem({ receiptDate: str });
  };

  // ── File → base64 ─────────────────────────────────────────────────────────
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]); // strip data:...;base64, prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // ── Process receipt via Gemini ─────────────────────────────────────────────
  const handleProcessReceipt = async () => {
    if (!activeItem) return;

    if (activeItem.type === 'text' && !activeItem.receiptText.trim()) {
      showNotification('Введіть текст чека', 'warning');
      return;
    }
    if (!activeItem.imageFile && !activeItem.receiptText.trim()) {
      showNotification('Додайте фото або введіть текст чека', 'warning');
      return;
    }

    updateActiveItem({ status: 'processing' });
    try {
      const categoriesForGemini = categories.map(cat => ({ id: cat.id, name: cat.name }));

      // Gather all images (primary + extras for multi-image mode)
      const allImageFiles = [
        ...(activeItem.imageFile ? [activeItem.imageFile] : []),
        ...(activeItem.extraImageFiles || []),
      ];
      const imageInputs: ReceiptImageInput[] = await Promise.all(
        allImageFiles.map(async (file) => {
          const base64 = await fileToBase64(file);
          return { base64Data: base64, mimeType: file.type || 'image/jpeg' };
        })
      );

      const extractResult = await geminiService.extractItemsFromReceipt(
        activeItem.receiptText,
        apiKey,
        categoriesForGemini,
        imageInputs[0],
        undefined,
        imageInputs.length > 1 ? imageInputs : undefined,
        accounts.map(acc => ({ id: acc.id, name: acc.name }))
      );

      // Перевіряємо чи був retry
      if (extractResult.retryInfo) {
        const { wasRetry, modelUsed, originalModel, failureReason } = extractResult.retryInfo;
        if (wasRetry) {
          showNotification(
            `Переспроба з моделлю ${modelUsed} (${originalModel} недоступна)`,
            'info'
          );
        } else if (originalModel) {
          showNotification(
            `Успішна переспроба обробки з моделлю ${modelUsed}`,
            'info'
          );
        }
      }

      if (extractResult.receipts.length === 0) {
        showNotification('Не вдалося витягти товари з чека', 'warning');
        updateActiveItem({ status: 'error' });
        return;
      }

      const [firstReceipt, ...otherReceipts] = extractResult.receipts;
      const stamp = Date.now();
      const withIds = (list: ExtractedItem[], r: number): ItemWithCategory[] =>
        list.map((item, index) => ({ ...item, id: `temp-${r}-${index}-${stamp}`, categoryId: item.categoryId }));

      const firstAccountId = firstReceipt.accountId || activeItem.accountId;

      // Додаткові чеки стають новими табами після активної
      const extraPoolItems = otherReceipts.map((r, i) => makePoolItem('text', {
        receiptName: r.receiptName || '',
        ...(r.receiptDate ? { receiptDate: r.receiptDate } : {}),
        accountId: r.accountId || firstAccountId,
        items: sortItems(withIds(r.items, i + 1)),
        status: 'done',
      }));

      setPoolItems(prev => {
        const idx = prev.findIndex(p => p.id === activePoolId);
        const updated = prev.map(p => p.id === activePoolId ? {
          ...p,
          items: sortItems(withIds(firstReceipt.items, 0)),
          receiptName: firstReceipt.receiptName || p.receiptName,
          ...(firstReceipt.receiptDate ? { receiptDate: firstReceipt.receiptDate } : {}),
          accountId: firstAccountId,
          type: (p.imageFile ? 'image' : 'text') as 'image' | 'text',
          status: 'done' as const,
        } : p);
        updated.splice(idx + 1, 0, ...extraPoolItems);
        return updated;
      });

      const allItems = extractResult.receipts.reduce<ExtractedItem[]>((acc, r) => acc.concat(r.items), []);
      const categorizedCount = allItems.filter(i => i.categoryId).length;
      const receiptsInfo = extractResult.receipts.length > 1 ? ` у ${extractResult.receipts.length} чеках` : '';

      showNotification(
        `Витягнуто ${allItems.length} товарів${receiptsInfo}${categorizedCount > 0 ? `, ${categorizedCount} з категоріями` : ''}`,
        'success'
      );
    } catch (error) {
      console.error('Помилка обробки чека:', error);
      showNotification('Помилка обробки чека через GEMINI API', 'error');
      updateActiveItem({ status: 'error' });
    }
  };

  // ── Item manipulation (operate on active pool item) ─────────────────────────
  const handleCategoryChange = (itemId: string, categoryId: string | undefined) => {
    updateActiveItem({ items: items.map(item =>
      item.id === itemId ? { ...item, categoryId: categoryId || undefined } : item
    )});
  };

  const handleToggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.id)));
    }
  };

  const handleApplyBulkCategory = () => {
    if (!bulkCategoryId) {
      showNotification('Оберіть категорію', 'warning');
      return;
    }

    const categoryId = bulkCategoryId === '' ? undefined : bulkCategoryId;
    updateActiveItem({ items: items.map(item =>
      selectedItems.has(item.id) ? { ...item, categoryId } : item
    )});
    setSelectedItems(new Set());
    setBulkCategoryId('');
    showNotification('Категорію призначено', 'success');
  };

  const handleStartEditName = (itemId: string, currentName: string) => {
    setEditingNameItemId(itemId);
    setEditingNameValue(currentName);
  };

  const handleSelectSuggestion = (value: string) => {
    if (!editingNameItemId) return;
    updateActiveItem({ items: items.map(item =>
      item.id === editingNameItemId ? { ...item, name: value.trim() } : item
    )});
    setEditingNameItemId(null);
    setEditingNameValue('');
    setHighlightedSuggestion(null);
  };

  const handleHighlightChange = (suggestion: PositionSuggestionResponse | null) => {
    setHighlightedSuggestion(suggestion ? suggestion.name : null);
  };

  // Обчислити текст для оверлею (тільки частина після введеного тексту)
  const getOverlayText = () => {
    if (!highlightedSuggestion || !editingNameValue || editingNameValue.trim().length === 0) return null;
    const lowerItemName = editingNameValue.toLowerCase().trim();
    const lowerSuggestion = highlightedSuggestion.toLowerCase();
    
    // Знайти позицію, з якої починається відмінність
    if (lowerSuggestion.startsWith(lowerItemName) && lowerItemName.length > 0) {
      return highlightedSuggestion.substring(editingNameValue.trim().length);
    }
    return null;
  };

  const overlayText = getOverlayText();

  // Отримати стилі з інпута для точного збігу
  useEffect(() => {
    if (!editingNameItemId || !nameInputRef.current) return;

    const updateStyles = () => {
      if (nameInputRef.current) {
        const computedStyle = window.getComputedStyle(nameInputRef.current);
        setInputStyles({
          fontFamily: computedStyle.fontFamily,
          fontSize: computedStyle.fontSize,
          fontStyle: computedStyle.fontStyle,
          fontWeight: computedStyle.fontWeight,
          letterSpacing: computedStyle.letterSpacing,
          lineHeight: computedStyle.lineHeight,
          padding: computedStyle.padding,
          paddingLeft: computedStyle.paddingLeft,
          paddingRight: computedStyle.paddingRight,
          paddingTop: computedStyle.paddingTop,
          paddingBottom: computedStyle.paddingBottom,
          borderLeftWidth: computedStyle.borderLeftWidth,
          borderRightWidth: computedStyle.borderRightWidth,
          borderTopWidth: computedStyle.borderTopWidth,
          borderBottomWidth: computedStyle.borderBottomWidth,
          height: computedStyle.height,
        });
      }
    };

    updateStyles();
    
    // Використати ResizeObserver для відстеження змін розміру
    const resizeObserver = new ResizeObserver(() => {
      updateStyles();
    });
    
    resizeObserver.observe(nameInputRef.current);
    
    // Оновити стилі при зміні розміру вікна
    window.addEventListener('resize', updateStyles);
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateStyles);
    };
  }, [editingNameItemId]);

  const handleSaveName = (itemId: string) => {
    if (!editingNameValue.trim()) {
      showNotification('Назва не може бути порожньою', 'warning');
      return;
    }
    updateActiveItem({ items: items.map(item =>
      item.id === itemId ? { ...item, name: editingNameValue.trim() } : item
    )});
    setEditingNameItemId(null);
    setEditingNameValue('');
    setHighlightedSuggestion(null);
  };

  const handleCancelEditName = () => {
    setEditingNameItemId(null);
    setEditingNameValue('');
    setHighlightedSuggestion(null);
  };

  const handleDeleteItem = (itemId: string) => {
    updateActiveItem({ items: items.filter(item => item.id !== itemId) });
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  };

  const handleDeleteSelected = () => {
    updateActiveItem({ items: items.filter(item => !selectedItems.has(item.id)) });
    setSelectedItems(new Set());
  };

  const handleSortItems = () => {
    updateActiveItem({ items: sortItems(items) });
  };

  // ── Create receipt(s) ───────────────────────────────────────────────────────
  const createReceiptFromPoolItem = async (poolItem: ReceiptPoolItem): Promise<string> => {
    const finalName = poolItem.receiptName.trim() || `Чек від ${new Date().toLocaleDateString('uk-UA')}`;
    const dateObj = poolItem.receiptDate === todayDateStr() ? new Date() : new Date(poolItem.receiptDate + 'T00:00:00');
    const receipt = await receiptService.createWithPositions({
      name: finalName,
      accountId: poolItem.accountId,
      type: 0,
      date: toLocalISOString(dateObj),
      positions: poolItem.items.map(item => ({
        name: item.name,
        amount: item.price,
        isBought: true,
        categoryId: item.categoryId!,
      })),
    });
    return receipt.id;
  };

  const handleCreateReceipt = async () => {
    if (!activeItem) return;
    if (!selectedAccountId) {
      showNotification('Оберіть рахунок', 'warning');
      return;
    }
    if (items.length === 0) {
      showNotification('Немає товарів для створення чеку', 'warning');
      return;
    }

    const firstUncategorized = items.find(item => !item.categoryId);
    if (firstUncategorized) {
      const el = document.getElementById(`receipt-item-${firstUncategorized.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('receipt-ocr-item-highlight');
        setTimeout(() => el.classList.remove('receipt-ocr-item-highlight'), 2000);
      }
      showNotification('Всі товари повинні мати категорію', 'warning');
      return;
    }

    updateActiveItem({ isCreating: true });
    try {
      await createReceiptFromPoolItem(activeItem);
      showNotification('Чек успішно створено', 'success');
      const remaining = poolItems.filter(p => p.id !== activeItem.id);
      if (remaining.length === 0) {
        onClose();
      } else {
        setPoolItems(remaining);
        setActivePoolId(remaining[0].id);
      }
    } catch (error) {
      console.error('Помилка створення чеку:', error);
      showNotification('Помилка створення чеку', 'error');
      updateActiveItem({ isCreating: false });
    }
  };

  const handleCreateAllReceipts = async () => {
    const readyItems = poolItems.filter(p =>
      p.status === 'done' && p.items.length > 0 && p.items.every(i => i.categoryId) && p.accountId
    );
    if (readyItems.length === 0) {
      showNotification('Немає готових чеків для створення', 'warning');
      return;
    }
    setPoolItems(prev => prev.map(p =>
      readyItems.find(r => r.id === p.id) ? { ...p, isCreating: true } : p
    ));
    const successIds = new Set<string>();
    for (const poolItem of readyItems) {
      try {
        await createReceiptFromPoolItem(poolItem);
        successIds.add(poolItem.id);
      } catch (error) {
        console.error(`Помилка створення чеку ${poolItem.id}:`, error);
        setPoolItems(prev => prev.map(p =>
          p.id === poolItem.id ? { ...p, isCreating: false } : p
        ));
      }
    }
    const createdCount = successIds.size;
    const failedItems = readyItems.filter(p => !successIds.has(p.id));
    if (createdCount > 0) {
      showNotification(
        `Успішно створено ${createdCount} чек${createdCount === 1 ? '' : createdCount < 5 ? 'и' : 'ів'}`,
        failedItems.length > 0 ? 'warning' : 'success'
      );
    }
    if (failedItems.length === 0) {
      onClose();
    } else {
      // Remove successfully created ones, keep only failed for retry
      setPoolItems(prev => prev.filter(p => !successIds.has(p.id)));
      setActivePoolId(failedItems[0].id);
      showNotification(`${failedItems.length} чек${failedItems.length < 5 ? 'и' : 'ів'} не вдалося створити`, 'error');
    }
  };

  const totalAmount = items.reduce((sum, item) => sum + item.price, 0);

  // Ready to create: done items with all items categorised and account selected
  const readyCount = poolItems.filter(p =>
    p.status === 'done' && p.items.length > 0 && p.items.every(i => i.categoryId) && p.accountId
  ).length;

  const anyCreating = poolItems.some(p => p.isCreating);

  const handleClose = () => {
    const hasData = poolItems.some(p => p.items.length > 0 || p.receiptText.trim() || p.imageFile);
    if (hasData) {
      setShowConfirmClose(true);
      return;
    }
    onClose();
  };

  const handleConfirmClose = () => {
    setShowConfirmClose(false);
    poolItems.forEach(p => {
      if (p.imagePreviewUrl) URL.revokeObjectURL(p.imagePreviewUrl);
      p.extraImagePreviewUrls?.forEach(url => URL.revokeObjectURL(url));
    });
    onClose();
  };;

  const handleCancelClose = () => {
    setShowConfirmClose(false);
  };

  // ── Pool tab helpers ────────────────────────────────────────────────────────
  const getPoolTabLabel = (p: ReceiptPoolItem): string => {
    if (p.receiptName) return p.receiptName;
    if (p.imageFile) return p.imageFile.name.replace(/\.[^/.]+$/, '');
    return p.receiptText.trim() ? 'Текст' : 'Новий чек';
  };

  const statusDotColor: Record<ReceiptPoolItem['status'], string> = {
    pending: 'rgba(255,255,255,0.25)',
    processing: 'var(--primary, #3b82f6)',
    done: 'var(--success, #22c55e)',
    error: 'var(--danger, #ef4444)',
    created: 'rgba(255,255,255,0.25)',
  };

  return (
    <>
    {/* Hidden file inputs */}
    <input
      ref={addImageInputRef}
      type="file"
      accept="image/*"
      multiple
      style={{ display: 'none' }}
      onChange={handleImageFilesAdded}
    />
    <input
      ref={replaceImageInputRef}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={handleImageFileReplaced}
    />
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="OCR чеків"
      size="lg"
      footer={(
        <div className="receipt-ocr-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={anyCreating}
          >
            Скасувати
          </button>
          {items.length > 0 && (
            <button
              className="btn btn-primary"
              onClick={handleCreateReceipt}
              disabled={isCreating || anyCreating || !selectedAccountId || items.some(item => !item.categoryId)}
              title={items.some(item => !item.categoryId) ? 'Всі товари повинні мати категорію' : ''}
            >
              {isCreating ? 'Створення...' : 'Створити чек'}
            </button>
          )}
          {readyCount > 1 && (
            <button
              className="btn btn-success"
              onClick={handleCreateAllReceipts}
              disabled={anyCreating}
            >
              {anyCreating ? 'Створення...' : `Створити всі (${readyCount})`}
            </button>
          )}
        </div>
      )}
    >
      <div className="receipt-ocr-modal-content">

        {/* ── Pool bar ─────────────────────────────────────────────────────── */}
        <div className="receipt-pool-bar">
          <div className="receipt-pool-tabs">
            {poolItems.map(p => (
              <div
                key={p.id}
                className={`receipt-pool-tab${p.id === activePoolId ? ' active' : ''}${p.status === 'created' ? ' created' : ''}`}
                onClick={() => setActivePoolId(p.id)}
                onMouseDown={(e) => { if (e.button === 1) { e.preventDefault(); handleRemovePoolItem(p.id); } }}
                title={getPoolTabLabel(p)}
              >
                {p.imageFile && p.imagePreviewUrl ? (
                  <img src={p.imagePreviewUrl} alt="" className="receipt-pool-tab-thumb" />
                ) : (
                  <span className="receipt-pool-tab-icon">
                    {p.imageFile ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                      </svg>
                    )}
                  </span>
                )}
                <span className="receipt-pool-tab-label">{getPoolTabLabel(p)}</span>
                <span
                  className="receipt-pool-tab-dot"
                  style={{ background: statusDotColor[p.status] }}
                />
                {poolItems.length > 1 && (
                  <button
                    className="receipt-pool-tab-remove"
                    onClick={e => { e.stopPropagation(); handleRemovePoolItem(p.id); }}
                    title="Видалити"
                    type="button"
                  >×</button>
                )}
              </div>
            ))}
          </div>
          <div className="receipt-pool-actions">
            <button
              className="receipt-pool-add-btn receipt-pool-add-plus"
              onClick={handleAddNewReceipt}
              title="Додати чек"
              type="button"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* ── Active pool item content ──────────────────────────────────────── */}
        {activeItem && (
          <>
            {/* Created banner */}
            {activeItem.status === 'created' && (
              <div className="receipt-pool-created-banner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Чек успішно створено
              </div>
            )}

            {/* Combined input layout for pending items without an image yet */}
            {activeItem.status === 'pending' && !activeItem.imageFile && activeItem.items.length === 0 ? (
              <div className="receipt-ocr-section">
                <div className="receipt-add-panel-layout">
                  {/* Left: photo */}
                  <div className="receipt-add-panel-left">
                    <label className="receipt-ocr-label">Фото чека</label>
                    {!activeItem.imageFile ? (
                      <button
                        type="button"
                        className={`receipt-add-panel-dropzone${pendingDragOver ? ' drag-over' : ''}`}
                        onClick={() => addImageInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setPendingDragOver(true); }}
                        onDragLeave={() => setPendingDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setPendingDragOver(false);
                          Array.from(e.dataTransfer.files)
                            .filter(f => f.type.startsWith('image/'))
                            .forEach(addImageToActiveItem);
                        }}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/>
                          <line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span>Натисніть або перетягніть фото</span>
                        <small>або вставте з буфера (⌘V)</small>
                      </button>
                    ) : (
                      <div className="receipt-add-panel-thumbs">
                        {[activeItem.imagePreviewUrl!, ...(activeItem.extraImagePreviewUrls || [])].map((url, idx) => (
                          <div key={idx} className="receipt-add-panel-thumb">
                            <img src={url} alt={`Фото ${idx + 1}`} />
                            <button type="button" className="receipt-add-panel-thumb-remove" onClick={() => removeImageFromActiveItem(idx)}>×</button>
                          </div>
                        ))}
                        <button type="button" className="receipt-add-panel-thumb receipt-add-panel-thumb-add"
                          onClick={() => addImageInputRef.current?.click()}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Right: text */}
                  <div className="receipt-add-panel-right">
                    <label className="receipt-ocr-label">Текст чека <span style={{opacity: 0.5, fontWeight: 400}}>(необов'язково)</span></label>
                    <textarea
                      className="receipt-ocr-textarea"
                      value={receiptText}
                      onChange={(e) => updateActiveItem({ receiptText: e.target.value })}
                      placeholder="Вставте текст чека, якщо є. Можна вказати дату та рахунок..."
                      rows={5}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
            {/* Image source */}
            {activeItem.imageFile && (
              <div className="receipt-ocr-section">
                {activeItem.imagePreviewUrl ? (
                  <div className="receipt-image-preview-wrap">
                    {/* Multi-image grid if extras exist */}
                    {activeItem.extraImageFiles && activeItem.extraImageFiles.length > 0 ? (
                      <div className="receipt-add-panel-thumbs">
                        {[activeItem.imagePreviewUrl, ...(activeItem.extraImagePreviewUrls || [])].map((url, idx) => (
                          <div key={idx} className="receipt-add-panel-thumb">
                            <img src={url} alt={`Фото ${idx + 1}`} />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <img src={activeItem.imagePreviewUrl} alt="Фото чека" className="receipt-image-preview" />
                    )}
                    <button
                      className="receipt-image-replace-btn"
                      onClick={() => handleReplaceImage(activeItem.id)}
                      type="button"
                    >
                      Замінити фото
                    </button>
                  </div>
                ) : (
                  <button
                    className="receipt-image-upload-placeholder"
                    onClick={handleAddImageItems}
                    type="button"
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                    Оберіть фото чека
                  </button>
                )}
              </div>
            )}

            {/* Text textarea */}
            {!activeItem.imageFile && (
              <div className="receipt-ocr-section">
                <label className="receipt-ocr-label">Текст чека (з OCR):</label>
                <textarea
                  className="receipt-ocr-textarea"
                  value={receiptText}
                  onChange={(e) => updateActiveItem({ receiptText: e.target.value })}
                  placeholder="Вставте текст чека, отриманий з OCR..."
                  rows={6}
                  disabled={isProcessing}
                />
              </div>
            )}

            {/* Optional hint for image type */}
            {activeItem.imageFile && activeItem.imagePreviewUrl && (
              <div className="receipt-ocr-section">
                <label className="receipt-ocr-label" style={{ opacity: 0.65, fontSize: '0.85rem' }}>
                  Підказка для AI (необов'язково):
                </label>
                <textarea
                  className="receipt-ocr-textarea"
                  style={{ minHeight: 'unset' }}
                  value={receiptText}
                  onChange={(e) => updateActiveItem({ receiptText: e.target.value })}
                  placeholder="Наприклад: рахунок Monobank, дата 12.05 або вчора..."
                  rows={2}
                  disabled={isProcessing}
                />
              </div>
            )}

            </>
            )}

            <button
              className="btn btn-primary receipt-ocr-process-btn"
              onClick={handleProcessReceipt}
              disabled={isProcessing || (!activeItem.imageFile && !receiptText.trim())}
            >
              {isProcessing ? 'Обробка...' : 'Обробити через GEMINI'}
            </button>

        {items.length > 0 && (
          <>
            <div className="receipt-ocr-section">
              <label className="receipt-ocr-label">Назва чека:</label>
              <input
                type="text"
                className="form-input"
                value={receiptName}
                onChange={(e) => updateActiveItem({ receiptName: e.target.value })}
                placeholder="Введіть назву чека (необов'язково)"
              />
            </div>

            <div className="receipt-ocr-section">
              <label className="receipt-ocr-label">Рахунок:</label>
              <GlassSelect
                options={accounts.map(account => ({
                  value: account.id,
                  label: account.name
                }))}
                value={selectedAccountId}
                onChange={(val) => updateActiveItem({ accountId: val })}
                placeholder="Оберіть рахунок"
                searchable
              />
            </div>

            <div className="receipt-ocr-section">
              <label className="receipt-ocr-label">Дата чека:</label>
              <div className="receipt-date-row">
                <button
                  className="receipt-date-arrow"
                  type="button"
                  onClick={() => handleShiftDate(-1)}
                  title="Попередній день"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div className="date-input-wrapper" style={{ flex: 1 }}>
                  <input
                    type="date"
                    id="ocr-receipt-date"
                    className="form-input date-input-custom"
                    value={receiptDate}
                    onChange={(e) => updateActiveItem({ receiptDate: e.target.value })}
                    autoComplete="off"
                  />
                  <label htmlFor="ocr-receipt-date" className="date-input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                  </label>
                </div>
                <button
                  className="receipt-date-arrow"
                  type="button"
                  onClick={() => handleShiftDate(+1)}
                  title="Наступний день"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="receipt-ocr-items-section">
              <div className="receipt-ocr-items-header">
                <div>
                  <h3>Товари ({items.length})</h3>
                  <div className="receipt-ocr-header-actions">
                    <button
                      className="receipt-ocr-select-all-btn"
                      onClick={handleSelectAll}
                      type="button"
                    >
                      {selectedItems.size === items.length ? 'Скасувати вибір' : 'Вибрати всі'}
                    </button>
                    <button
                      className="receipt-ocr-sort-btn"
                      onClick={handleSortItems}
                      type="button"
                      title="Сортувати за категорією"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M3 12h12M3 18h6" />
                      </svg>
                      Сортувати
                    </button>
                  </div>
                </div>
                <div className="receipt-ocr-total">
                  Загальна сума: <strong>{formatCurrency(totalAmount)}</strong>
                </div>
              </div>

              {selectedItems.size > 0 && (
                <div className="receipt-ocr-bulk-actions">
                  <div className="receipt-ocr-bulk-info">
                    Вибрано: {selectedItems.size} товарів
                  </div>
                  <div className="receipt-ocr-bulk-controls">
                    <GlassSelect
                      options={categories.map(cat => ({
                        value: cat.id,
                        label: cat.name
                      }))}
                      value={bulkCategoryId}
                      onChange={setBulkCategoryId}
                      placeholder="Оберіть категорію"
                      searchable
                      className="receipt-ocr-bulk-select"
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleApplyBulkCategory}
                      disabled={!bulkCategoryId}
                    >
                      Призначити вибраним
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleDeleteSelected}
                      type="button"
                    >
                      Видалити вибрані
                    </button>
                  </div>
                </div>
              )}

              <div className="receipt-ocr-items-list">
                {items.map((item) => {
                  const category = item.categoryId 
                    ? categories.find(c => c.id === item.categoryId)
                    : null;
                  const isSelected = selectedItems.has(item.id);

                  return (
                    <div 
                      key={item.id}
                      id={`receipt-item-${item.id}`}
                      className={`receipt-ocr-item ${isSelected ? 'selected' : ''}${!item.categoryId ? ' no-category' : ''}`}
                    >
                      <div
                        className={`receipt-ocr-item-checkbox ${isSelected ? 'checked' : ''}`}
                        onClick={() => handleToggleItemSelection(item.id)}
                      />
                      <button
                        className="receipt-ocr-item-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteItem(item.id);
                        }}
                        title="Видалити товар"
                        type="button"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                      <div className="receipt-ocr-item-content">
                        <div className="receipt-ocr-item-info">
                          {editingNameItemId === item.id ? (
                            <div className="receipt-ocr-item-name-edit" style={{ position: 'relative' }}>
                              <input
                                ref={nameInputRef}
                                type="text"
                                className="receipt-ocr-item-name-input"
                                value={editingNameValue}
                                onChange={(e) => setEditingNameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    // PositionSuggestionsPanel обробить Enter в capture phase, якщо є виділений саджест
                                    // Якщо подія дійшла сюди, значить немає виділеного саджеста - зберігаємо поточне значення
                                    e.preventDefault();
                                    handleSaveName(item.id);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    handleCancelEditName();
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                                autoComplete="off"
                              />
                              {overlayText && nameInputRef.current && Object.keys(inputStyles).length > 0 && (
                                <span 
                                  className="add-item-input-overlay"
                                  style={{
                                    position: 'absolute',
                                    left: '2px',
                                    top: 0,
                                    pointerEvents: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: 'var(--primary)',
                                    opacity: 0.7,
                                    whiteSpace: 'nowrap',
                                    boxSizing: 'border-box',
                                    overflow: 'hidden',
                                    width: '100%',
                                    fontFamily: inputStyles.fontFamily,
                                    fontSize: inputStyles.fontSize,
                                    fontStyle: inputStyles.fontStyle,
                                    fontWeight: inputStyles.fontWeight,
                                    letterSpacing: inputStyles.letterSpacing,
                                    lineHeight: inputStyles.lineHeight,
                                    padding: inputStyles.padding,
                                    paddingLeft: inputStyles.paddingLeft,
                                    paddingRight: inputStyles.paddingRight,
                                    paddingTop: inputStyles.paddingTop,
                                    paddingBottom: inputStyles.paddingBottom,
                                    height: inputStyles.height,
                                  }}
                                >
                                  <span style={{ visibility: 'hidden', whiteSpace: 'pre' }}>{editingNameValue}</span>
                                  <span>{overlayText}</span>
                                </span>
                              )}
                              <PositionSuggestionsPanel
                                value={editingNameValue}
                                categoryId={item.categoryId}
                                onSelect={handleSelectSuggestion}
                                onHighlightChange={handleHighlightChange}
                                inputRef={nameInputRef}
                              />
                              <button
                                className="receipt-ocr-name-save"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveName(item.id);
                                }}
                                title="Зберегти"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              </button>
                              <button
                                className="receipt-ocr-name-cancel"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEditName();
                                }}
                                title="Скасувати"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18"></line>
                                  <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="receipt-ocr-item-name"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEditName(item.id, item.name);
                              }}
                              title="Натисніть для редагування"
                            >
                              {item.name}
                            </div>
                          )}
                          <div className="receipt-ocr-item-price">{formatCurrency(item.price)}</div>
                        </div>
                        {category && (
                          <div className="receipt-ocr-item-category-badge">
                            <span>{category.name}</span>
                            <button
                              className="receipt-ocr-category-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCategoryChange(item.id, undefined);
                              }}
                              title="Видалити категорію"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {!category && editingCategoryItemId !== item.id && (
                          <button
                            className="receipt-ocr-item-add-category"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCategoryItemId(item.id);
                            }}
                          >
                            + Додати категорію
                          </button>
                        )}
                        {editingCategoryItemId === item.id && (
                          <div className="receipt-ocr-item-category-edit" onClick={(e) => e.stopPropagation()}>
                            <GlassSelect
                              options={categories.map(cat => ({
                                value: cat.id,
                                label: cat.name
                              }))}
                              value={item.categoryId || ''}
                              onChange={(value) => {
                                handleCategoryChange(item.id, value || undefined);
                                setEditingCategoryItemId(null);
                              }}
                              placeholder="Оберіть категорію"
                              searchable
                            />
                            <button
                              className="receipt-ocr-category-cancel"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingCategoryItemId(null);
                              }}
                            >
                              Скасувати
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
        </>
      )}
      </div>
    </Modal>

    <ConfirmationModal
      isOpen={showConfirmClose}
      title={items.length > 0 ? "Закрити модальне вікно?" : "Закрити модальне вікно?"}
      message={items.length > 0 
        ? "AI вже згенерував результати обробки. Ви впевнені, що хочете закрити модальне вікно? Всі дані будуть втрачені."
        : "У вас є незбережений текст. Ви впевнені, що хочете закрити модальне вікно? Всі дані будуть втрачені."}
      confirmText="Так, закрити"
      cancelText="Скасувати"
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
      variant="warning"
    />
    </>
  );
};

export default ReceiptOCRModal;

